<template>
  <div class="uniform-filters">
    <c-loader v-if="!inited" :active="inited" label="Инициализация фильтров" :height="300"></c-loader>

    <div v-else>
      <transition name="slide">
        <div v-if="open" class="row">
          <div class="col col-12">
            <div class="form-group uniform-filters__status">
              <c-switcher v-model="form.state" type="group" class="h-switcher" :items="states" @input="onUpdateState" />
            </div>
          </div>
          <div class="col col-12">
            <div class="form-group uniform-filters__category">
              <c-input
                v-model="form.categories"
                type="select"
                :multiple="true"
                :items="catalog"
                :searchable="true"
                :with-categorized="true"
                :disabled-values="disabledCatalogItems"
                label="Категория/подкатегория/тип ТМЦ"
                placeholder="Все"
                :checkbox-value="form.categoriesExclude"
                :checkbox="true"
                :checkbox-type="'icon'"
                :checkbox-tooltip="{
                  disabledText: 'Фильтр по всем значениям в справочнике, кроме выбранных',
                  activeText: 'Выключить фильтр по всем значениям в справочнике, кроме выбранных',
                  notActiveText: 'Включить фильтр по всем значениям в справочнике, кроме выбранных',
                }"
                @checkboxChange="form.categoriesExclude = $event"
              />
            </div>
          </div>
          <div class="col col-12 col-md-4 col-xl-4">
            <div class="form-group uniform-filters__name">
              <c-input
                v-model="form.nameAndNumber"
                :clearable="true"
                label="Серийный номер/наименование ТМЦ"
                placeholder="Любое"
                @enter="apply"
              />
            </div>
          </div>
          <div class="col col-12 col-md-4 col-xl-4">
            <div class="form-group uniform-filters__workers">
              <c-input
                v-model="form.workers"
                type="select"
                :items="workers"
                :searchable="true"
                :multiple="true"
                label="Сотрудник, которому выдана ТМЦ"
                placeholder="Все"
              />
            </div>
          </div>
          <div class="col col-12 col-md-4 col-xl-4">
            <div class="form-group uniform-filters__status">
              <c-input
                v-model="form.status"
                type="select"
                :items="statuses"
                :clearable="true"
                label="Статус ТМЦ"
                placeholder="Все"
              />
            </div>
          </div>

          <div class="col col-12 col-md-4 col-xl-4">
            <div class="form-group uniform-filters__expiration">
              <c-input
                v-model="form.expiration"
                type="select"
                :items="expirationTypes"
                :clearable="true"
                label="По сроку годности ТМЦ"
                placeholder="Любой"
              />
            </div>
          </div>

          <div v-if="expirationDaysFilter" class="col col-12 col-md-4 col-xl-2">
            <div class="form-group uniform-filters__exp-days">
              <div class="d-flex">
                <c-input
                  v-model="form.expirationDays"
                  :clearable="true"
                  type="price-number"
                  :required="isSubmitted && expirationDaysError"
                  :required-message="isSubmitted && expirationDaysError"
                  :required-condition="isSubmitted && expirationDaysError"
                />
                <div class="uniform-filters__label">дней</div>
              </div>
            </div>
          </div>

          <div
            v-if="expirationPeriodFilter"
            class="col col-12 col-md-4 col-xl-3"
            :class="{
              'is-invalid': expirationDaysError,
            }"
          >
            <div class="form-group uniform-filters__exp-period">
              <c-input
                ref="expirationPeriod"
                v-model="form.expirationPeriod"
                label="Период истечения срока"
                placeholder="Выберите период"
                type="period-without-fixed-intervals"
                icon="calendar"
                :icon-right="true"
                @mounted="onExpirationPeriodMounted"
              />
            </div>
          </div>

          <div class="uniform-filters__actions">
            <c-button class="uniform-filters__reset" label="Сбросить" @click="reset" />
            <c-button class="uniform-filters__apply" label="Применить" :color="'primary'" @click="apply" />
          </div>
        </div>
      </transition>

      <template v-if="!isDesktop">
        <c-button
          v-if="open"
          class="uniform-filters__toggler"
          size="small"
          label="Скрыть фильтры"
          @click="open = false"
        />

        <c-button
          v-if="!open"
          class="uniform-filters__toggler"
          size="small"
          label="Показать фильтры"
          @click="open = true"
        />
      </template>
    </div>
  </div>
</template>

<script>
import CInput from "@/components/controls2/Input";
import CButton from "@/components/controls2/Button";
import CSwitcher from "@/components/controls2/Switcher";
import CLoader from "@/components/loader";

import { Breakpoint } from "@/utils/browser/breakpoint";

import {
  EXPIRATION_DAYS,
  EXPIRATION_PERIOD,
  EXPIRED,
  STATE_ACTIVE,
  STATE_ALL,
  STATE_WRITTEN_OFF,
  STATUS_FREE,
  STATUS_ISSUED,
  STATUS_WRITTEN_OFF,
  WITHOUT_EXPIRATION,
  WITH_EXPIRATION,
} from "../models/FiltersModel";

export default {
  components: { CInput, CButton, CSwitcher, CLoader },
  props: ["filters"],

  data() {
    return {
      inited: false,
      isSubmitted: false,
      open: true,
      statuses: [
        { value: STATUS_FREE, label: "Свободна" },
        { value: STATUS_ISSUED, label: "Выдана" },
        { value: STATUS_WRITTEN_OFF, label: "Списана" },
      ],
      expirationTypes: [
        { value: WITHOUT_EXPIRATION, label: "Без срока годности" },
        { value: WITH_EXPIRATION, label: "Со сроком годности" },
        { value: EXPIRED, label: "Истек срок годности" },
        { value: EXPIRATION_PERIOD, label: "Выходит срок годности" },
        { value: EXPIRATION_DAYS, label: "Истекает в течение" },
      ],
      states: [
        { value: STATE_ALL, label: "Все" },
        { value: STATE_ACTIVE, label: "Активные" },
        { value: STATE_WRITTEN_OFF, label: "Списанные" },
      ],

      form: {
        state: STATE_ALL,
        categories: [],
        categoriesExclude: false,
        nameAndNumber: "",
        workers: [],
        status: null,
        expiration: null,
        expirationDays: "",
        expirationPeriod: "",
      },

      isDesktop: true,
      disabledCatalogItems() {
        return [];
      },
    };
  },

  computed: {
    workers() {
      return this.$store.getters["actors/workersList"];
    },
    catalog() {
      const { catalog } = this.$store.state.uniformItems;

      const list = [];

      catalog.forEach((category) => {
        const {
          id: categoryId,
          name: categoryName,
          categories: subcategories = [],
          types: categoryTypes = [],
        } = category;

        const shortCategoryName = categoryName && categoryName.length > 10 ? `${categoryName.slice(0, 10)}...` : categoryName;

        const categoryModel = {
          value: `category-${categoryId}`,
          id: categoryId,
          label: categoryName,
          resultLabel: `${categoryName}`,
          isSubCategory: false,
          level: 0,
          catalogType: "category",
        };

        const notEmptySubcategories = subcategories.filter((subcategory) => subcategory.types.length);

        if (categoryTypes.length || notEmptySubcategories.length) {
          list.push(categoryModel);
        }

        notEmptySubcategories.forEach((subcategory) => {
          const { id, name: subcategoryName, types } = subcategory;

          const shortSubcategoryName =
            subcategoryName.length > 10 ? `${subcategoryName.slice(0, 10)}...` : subcategoryName;

          list.push({
            value: `subcategory-${id}`,
            id,
            label: subcategoryName,
            resultLabel: `<span class="catalog-category-name">${shortCategoryName}</span>/${subcategoryName}`,
            isSubCategory: false,
            level: 1,
            catalogType: "subcategory",
          });

          types.forEach((type) => {
            const { id: typeId, name: typeName } = type;
            list.push({
              value: `type-${typeId}`,
              id: typeId,
              label: typeName,
              resultLabel: `<span class="catalog-category-name">${shortCategoryName}</span><span class="catalog-category-name">/${shortSubcategoryName}</span>/${typeName}`,
              isSubCategory: true,
              level: 2,
              catalogType: "type",
            });
          });
        });

        categoryTypes.forEach((type) => {
          const { id: typeId, name: typeName } = type;
          list.push({
            value: `type-${typeId}`,
            id: typeId,
            label: typeName,
            resultLabel: `<span class="catalog-category-name">${shortCategoryName}</span>/${typeName}`,
            isSubCategory: true,
            level: 1,
            catalogType: "type",
          });
        });
      });

      return list;
    },
    expirationPeriodFilter() {
      return this.form.expiration && this.form.expiration.value === EXPIRATION_PERIOD;
    },
    expirationDaysFilter() {
      return this.form.expiration && this.form.expiration.value === EXPIRATION_DAYS;
    },
    expirationDaysError() {
      if (!this.expirationDaysFilter) return null;
      if (!this.form.expirationDays.length) return null;
      return +this.form.expirationDays === 0 ? "Значение должно быть больше 0" : null;
    },
    isValid() {
      return !this.expirationDaysError;
    },
  },

  watch: {
    "form.categories": function (v) {
      const children = [];

      v.forEach((item) => {
        const { level, value } = item;
        if (level === 2) return;

        let itemIndex = this.catalog.findIndex((i) => i.value === value);
        if (itemIndex === -1) return;

        itemIndex += 1;

        while (itemIndex < this.catalog.length) {
          const nextItem = this.catalog[itemIndex];
          if (nextItem.level <= level) break;
          children.push(nextItem.value);
          itemIndex += 1;
        }
      });

      this.disabledCatalogItems = children;

      const newValue = v.filter((item) => !children.includes(item.value));

      if (newValue.length < v.length) this.form.categories = newValue;

      if (!v.length) {
        this.form.categoriesExclude = false;
      }
    },

    "form.expiration": function () {
      if (!this.inited) return;
      this.form.expirationDays = "";
      this.form.expirationPeriod = "";
    },

    "isDesktop": function (v) {
      if (v) this.open = true;
    },
  },

  async created() {
    this.inited = false;

    const breakpoint = Breakpoint("(min-width: 768px)");
    const cb = (isDesktop) => {
      this.isDesktop = isDesktop;
    };
    breakpoint.subscribe(cb);
    this.syncFilters();

    this.$nextTick(() => {
      this.inited = true;
    });
  },

  methods: {
    syncFilters() {
      const {
        state,
        categories,
        subcategories,
        types,
        categoriesExclude,
        nameAndNumber,
        workers,
        status,
        expiration,
        expirationDays,
        expirationFrom,
        expirationTo,
      } = this.filters;

      this.form.state = state;

      const selectedCategories = categories
        .map((categoryId) =>
          this.catalog.find((item) => item.catalogType === "category" && item.id.toString() === categoryId.toString()),
        )
        .filter(Boolean);
      const selectedSubcategories = subcategories
        .map((categoryId) =>
          this.catalog.find(
            (item) => item.catalogType === "subcategory" && item.id.toString() === categoryId.toString(),
          ),
        )
        .filter(Boolean);
      const selectedTypes = types
        .map((categoryId) =>
          this.catalog.find((item) => item.catalogType === "type" && item.id.toString() === categoryId.toString()),
        )
        .filter(Boolean);

      this.form.categories = [...selectedCategories, ...selectedSubcategories, ...selectedTypes];

      this.form.categoriesExclude = !!categoriesExclude;

      this.form.nameAndNumber = nameAndNumber;
      this.form.workers = workers
        .map((workerId) => this.workers.find((w) => w.value.toString() === workerId.toString()))
        .filter(Boolean);

      this.form.status = this.statuses.find((i) => i.value === status);

      this.form.expirationDays = expirationDays;

      this.form.expirationPeriod = `${expirationFrom}-${expirationTo}`;

      this.form.expiration = this.expirationTypes.find((i) => i.value === expiration);
    },
    onExpirationPeriodMounted() {
      const period = this.form.expirationPeriod;
      if (!period) return;

      const ref = this.$refs.expirationPeriod;
      ref.dateValue = period;
    },
    getParams() {
      const {
        state,
        categories,
        categoriesExclude,
        nameAndNumber,
        workers,
        status,
        expiration,
        expirationDays,
        expirationPeriod,
      } = this.form;

      const selectedCategories = [];
      const selectedSubcategories = [];
      const selectedTypes = [];

      categories.forEach((item) => {
        const { id, catalogType } = item;
        if (catalogType === "category") selectedCategories.push(id);
        else if (catalogType === "subcategory") selectedSubcategories.push(id);
        else if (catalogType === "type") selectedTypes.push(id);
      });

      const [expirationFrom, expirationTo] = expirationPeriod.split("-");

      const params = {
        state,
        categories: selectedCategories,
        subcategories: selectedSubcategories,
        types: selectedTypes,

        categoriesExclude,
        nameAndNumber,
        workers: workers.map((w) => w.value),
        status: status && status.value,
        expiration: expiration && expiration.value,
        expirationDays,
        expirationFrom: (expirationFrom || "").trim(),
        expirationTo: (expirationTo || "").trim(),
      };

      return params;
    },
    apply() {
      this.isSubmitted = true;
      if (!this.isValid) return;

      this.isSubmitted = false;
      this.$emit("apply", this.getParams());
    },
    reset() {
      this.form.state = STATE_ALL;
      this.form.categories = [];
      this.form.categoriesExclude = false;
      this.form.nameAndNumber = "";
      this.form.workers = [];
      this.form.status = null;
      this.form.expiration = null;
      this.form.expirationDays = "";
      this.form.expirationPeriod = "";

      this.apply();
    },
    onUpdateState() {
      this.$nextTick(() => {
        this.apply();
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/styles/vars";
.uniform-filters {
  padding-bottom: 10px;

  /deep/ {
    .catalog-category-name {
      color: $color-label;
    }
  }

  &__category {
    padding-top: 6px;
  }

  &__exp-days {
    padding-top: 16px;
  }

  &__label {
    font-size: 15px;
    margin-left: 15px;
  }

  &__actions {
    display: flex;
    margin-left: auto;
    margin-bottom: 24px;
    align-self: flex-end;
  }

  &__reset {
    margin-right: 10px;
  }

  &__toggler {
    width: 100%;
    margin-bottom: 20px;
  }

  @include beforeBootstrapMd {
    &__actions {
      width: 100%;
    }

    &__apply,
    &__reset {
      margin-left: 10px;
      margin-right: 10px;
      flex-grow: 1;
    }
  }
}
</style>
